import { useEffect, useState } from 'react';
import { useRoute, useLocation, Link } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  ChevronLeft,
  Pencil,
  Trash2,
  Briefcase,
  Calendar,
  Eye,
  Power,
  Shield,
  UserCog,
  Mail,
  Phone,
  Search,
} from 'lucide-react';

import { teamService } from '@/services/api/team.service';
import { bookingService } from '@/services/api/booking.service';
import {
  useToggleUserActive,
  useDeleteUser,
} from '@/hooks/use-users';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { EditMemberDialog } from '@/components/team/EditMemberDialog';
import { cn } from '@/lib/utils';

import type { TeamMember } from '@/types/team';
import type { Provider, Service } from '@/types/booking';
import type { User } from '@/types/auth';
import type { BookingStatus } from '@/lib/status-colors';

type TimeTab = 'upcoming' | 'past';

function memberToUser(member: TeamMember): User {
  return {
    id: member.id,
    firstName: member.firstName,
    lastName: member.lastName,
    email: member.email,
    phone: member.phone,
    role: member.role,
    active: member.active,
    createdAt: member.createdAt,
    updatedAt: member.createdAt,
    organizationId: 0,
  } as unknown as User;
}

function memberToProvider(member: TeamMember): Provider | null {
  if (!member.provider) return null;
  return {
    id: member.provider.id,
    publicId: member.provider.publicId,
    bio: member.provider.bio,
    specialization: member.provider.specialization,
    active: member.provider.active,
    services: member.provider.services.map((s) => ({
      id: s.id,
      name: s.name,
      duration: s.durationMinutes,
    })),
    userId: member.id,
    organizationId: 0,
  } as unknown as Provider;
}

export default function AdminTeamDetailPage() {
  const { t } = useTranslation(['team', 'common', 'bookings', 'invitations']);
  const [, params] = useRoute('/admin/team/:id');
  const [, setLocation] = useLocation();
  const { user: currentUser } = useAuth();
  const { toast } = useToast();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [activeBookingTab, setActiveBookingTab] = useState<TimeTab>('upcoming');
  const [bookingSearch, setBookingSearch] = useState('');
  const [debouncedBookingSearch, setDebouncedBookingSearch] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedBookingSearch(bookingSearch.trim()), 300);
    return () => clearTimeout(handle);
  }, [bookingSearch]);

  const memberId = (params as { id?: string })?.id || null;

  const {
    data: member,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['admin-team-member', memberId],
    queryFn: () => teamService.getMember(memberId!),
    enabled: !!memberId,
  });

  const { data: services = [] } = useQuery({
    queryKey: ['services'],
    queryFn: () => bookingService.getServices(),
  });

  const providerId = member?.provider?.id ?? null;

  const {
    data: bookingsData,
    isLoading: isLoadingBookings,
  } = useQuery({
    queryKey: ['admin-team-member-bookings', providerId, activeBookingTab, debouncedBookingSearch],
    queryFn: () =>
      bookingService.getAdminBookings({
        provider_id: providerId!,
        time_range: activeBookingTab,
        sort_dir: activeBookingTab === 'past' ? 'desc' : 'asc',
        ...(debouncedBookingSearch ? { search: debouncedBookingSearch } : {}),
        per_page: 20,
      }),
    enabled: !!providerId,
  });

  const toggleActiveMutation = useToggleUserActive();
  const deleteMutation = useDeleteUser();

  const handleDelete = async () => {
    if (!member) return;
    const name = `${member.firstName} ${member.lastName}`;
    const confirmed = window.confirm(
      t('invitations:confirmations.removeMember', { name }) as string
    );
    if (!confirmed) return;
    await deleteMutation.mutateAsync(member.id, {
      onSuccess: () => {
        toast({ title: t('common:toasts.deleteSuccess', 'Deleted') });
        setLocation('/admin/team');
      },
      onError: () => {
        toast({
          title: t('common:errors.generic', 'Something went wrong'),
          variant: 'destructive',
        });
      },
    });
  };

  const handleToggleActive = async () => {
    if (!member || member.id === currentUser?.id) return;
    await toggleActiveMutation.mutateAsync(member.id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  if (isError || !member) {
    return (
      <div className="space-y-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setLocation('/admin/team')}
          iconLeft={<ChevronLeft />}
        >
          {t('team:detail.back', 'All team members')}
        </Button>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-soft">
              {t('team:detail.notFound', "We couldn't find that team member.")}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isSelf = member.id === currentUser?.id;
  const provider = member.provider;
  const bookings = bookingsData?.bookings ?? [];
  const dateFormats = getDateFormats();
  const locale = getDateLocale();

  return (
    <div className="space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setLocation('/admin/team')}
        iconLeft={<ChevronLeft />}
        aria-label={t('team:detail.backAriaLabel', 'Back to team list')}
      >
        {t('team:detail.back', 'All team members')}
      </Button>

      {/* Header card */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            {/* Identity */}
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <Avatar className="h-16 w-16 shrink-0">
                <AvatarFallback className="bg-brand-100 text-brand-800 text-lg font-semibold">
                  {`${member.firstName?.[0] ?? ''}${member.lastName?.[0] ?? ''}`.toUpperCase() || '?'}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <h1 className="text-h1 text-ink leading-tight">
                    {member.firstName} {member.lastName}
                  </h1>
                  {isSelf && (
                    <Badge variant="outline" className="text-xs">
                      {t('team:badges.you', 'You')}
                    </Badge>
                  )}
                </div>

                {provider?.specialization && (
                  <p className="text-sm text-soft">{provider.specialization}</p>
                )}

                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="flex items-center gap-1">
                    {member.role === 'admin' ? (
                      <Shield className="w-3 h-3" />
                    ) : (
                      <UserCog className="w-3 h-3" />
                    )}
                    {t(`team:badges.${member.role}`, member.role)}
                  </Badge>
                  {provider && (
                    <Badge variant="secondary" className="flex items-center gap-1 bg-brand-100 text-brand-800">
                      <Briefcase className="w-3 h-3" />
                      {t('team:badges.provider', 'Provider')}
                    </Badge>
                  )}
                  <Badge
                    className={
                      member.active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-surface-2 text-ink'
                    }
                  >
                    {member.active
                      ? t('common:status.active', 'Active')
                      : t('common:status.inactive', 'Inactive')}
                  </Badge>
                </div>

                <div className="text-sm text-soft flex flex-wrap items-center gap-x-4 gap-y-1 pt-1">
                  <a
                    href={`mailto:${member.email}`}
                    className="inline-flex items-center gap-1.5 hover:text-ink transition-colors"
                  >
                    <Mail className="h-4 w-4" />
                    {member.email}
                  </a>
                  {member.phone && (
                    <a
                      href={`tel:${member.phone}`}
                      className="inline-flex items-center gap-1.5 hover:text-ink transition-colors"
                    >
                      <Phone className="h-4 w-4" />
                      {member.phone}
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
              <Button variant="outline" onClick={() => setIsEditOpen(true)}>
                <Pencil className="h-4 w-4 mr-2" />
                {t('common:actions.edit', 'Edit')}
              </Button>

              {!isSelf && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleToggleActive}
                    disabled={toggleActiveMutation.isPending}
                  >
                    <Power className="h-4 w-4 mr-2" />
                    {member.active
                      ? t('common:actions.deactivate', 'Deactivate')
                      : t('common:actions.activate', 'Activate')}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('common:actions.remove', 'Remove')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Services */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              {t('team:detail.servicesTitle', 'Services')}
            </CardTitle>
            {provider && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditOpen(true)}
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                {t('team:detail.editServices', 'Edit Services')}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!provider ? (
            <p className="text-sm text-soft">
              {t('team:detail.notProviderHint', 'This team member is not yet a provider. Edit to assign services.')}
            </p>
          ) : provider.services.length === 0 ? (
            <p className="text-sm text-soft">
              {t('team:detail.noServices', 'Not offering any services yet.')}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {provider.services.map((s) => (
                <Badge key={s.id} variant="outline" className="text-sm">
                  {s.name}
                  {s.durationMinutes && (
                    <span className="ml-1.5 text-xs text-soft">
                      · {s.durationMinutes} min
                    </span>
                  )}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bookings */}
      {provider && (
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {t('team:detail.bookingsTitle', 'Bookings')}
            </CardTitle>
            <div
              role="group"
              aria-label={t('team:detail.bookingTabs.ariaLabel', 'Booking time range')}
              className="flex items-center gap-2 pt-2"
            >
              <button
                type="button"
                onClick={() => setActiveBookingTab('upcoming')}
                aria-pressed={activeBookingTab === 'upcoming'}
                className={cn(
                  'inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap',
                  'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1',
                  activeBookingTab === 'upcoming'
                    ? 'bg-brand-500 text-white shadow-sm'
                    : 'bg-surface-2 text-ink hover:bg-surface-2'
                )}
              >
                {t('team:detail.bookingTabs.upcoming', 'Upcoming')}
                {provider.bookingCounts.upcoming > 0 && (
                  <span className="ml-1.5 text-xs opacity-80">· {provider.bookingCounts.upcoming}</span>
                )}
              </button>
              <button
                type="button"
                onClick={() => setActiveBookingTab('past')}
                aria-pressed={activeBookingTab === 'past'}
                className={cn(
                  'inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap',
                  'focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1',
                  activeBookingTab === 'past'
                    ? 'bg-brand-500 text-white shadow-sm'
                    : 'bg-surface-2 text-ink hover:bg-surface-2'
                )}
              >
                {t('team:detail.bookingTabs.past', 'Past')}
                {provider.bookingCounts.past > 0 && (
                  <span className="ml-1.5 text-xs opacity-80">· {provider.bookingCounts.past}</span>
                )}
              </button>
            </div>

            {/* Search input — server-side, scoped to this provider's bookings */}
            <div className="relative pt-2 max-w-md">
              <Search className="absolute left-3 top-1/2 mt-1 -translate-y-1/2 h-4 w-4 text-soft pointer-events-none" />
              <Input
                type="search"
                value={bookingSearch}
                onChange={(e) => setBookingSearch(e.target.value)}
                placeholder={t('team:detail.bookingSearch.placeholder', 'Search bookings by name, email, or service…')}
                aria-label={t('team:detail.bookingSearch.ariaLabel', 'Search bookings')}
                className="pl-10 h-10"
                data-testid="team-detail-booking-search"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {isLoadingBookings ? (
              <div className="px-4 py-12 flex justify-center">
                <Spinner className="w-6 h-6" />
              </div>
            ) : bookings.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <Calendar className="h-8 w-8 mx-auto mb-3 text-soft" />
                <p className="text-soft">
                  {debouncedBookingSearch
                    ? t('team:detail.bookingSearch.noResults', { query: debouncedBookingSearch, defaultValue: 'No bookings match "{{query}}".' })
                    : activeBookingTab === 'upcoming'
                    ? t('team:detail.noUpcomingBookings', 'No upcoming bookings.')
                    : t('team:detail.noPastBookings', 'No past bookings.')}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line bg-surface-2">
                      <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap">
                        {t('bookings:table.date', 'Date')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden sm:table-cell">
                        {t('bookings:table.customer', 'Customer')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap hidden md:table-cell">
                        {t('bookings:table.service', 'Service')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-left font-semibold text-soft whitespace-nowrap">
                        {t('bookings:table.status', 'Status')}
                      </th>
                      <th scope="col" className="px-4 py-3 text-right">
                        <span className="sr-only">{t('bookings:table.view', 'View')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => {
                      const startTime = booking.start_time;
                      const customerName = `${booking.customerFirstName ?? ''} ${booking.customerLastName ?? ''}`.trim();
                      const status = booking.status as BookingStatus;
                      return (
                        <tr
                          key={booking.id}
                          className="border-b border-line last:border-0 hover:bg-surface-2 transition-colors"
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            {startTime ? (
                              <div className="flex flex-col leading-tight">
                                <span>{format(parseISO(startTime), dateFormats.medium, { locale })}</span>
                                <span className="text-xs text-soft">
                                  {format(parseISO(startTime), 'p', { locale })}
                                </span>
                              </div>
                            ) : (
                              <span className="text-soft">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell text-ink">
                            {customerName || '—'}
                          </td>
                          <td className="px-4 py-3 hidden md:table-cell text-ink">
                            {booking.service?.name ?? '—'}
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={status}>
                              {t(`bookings:status.${status === 'no_show' ? 'noShow' : status}`)}
                            </StatusBadge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <Link
                              href={`/admin/bookings/${booking.publicId ?? booking.id}`}
                              aria-label={t('bookings:actions.viewAriaLabel', { code: booking.bookingCode ?? booking.id, defaultValue: 'View booking {{code}}' })}
                              className="inline-flex items-center justify-center h-9 w-9 rounded-md text-soft hover:bg-surface-2 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500"
                            >
                              <Eye className="h-4 w-4" />
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <EditMemberDialog
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        member={memberToUser(member)}
        provider={memberToProvider(member)}
        services={services as Service[]}
      />
    </div>
  );
}
